import { useEffect, useRef, useState } from 'react';

import { Alert, Box, Button, ButtonGroup, Heading, Modal, TextButton, Toast, useToast } from '@zillow/constellation-10';

import { useQueryClient } from '@zg-rentals/react-query5';
import {
  useRentalManagerUserPropertyDeactivateWebApiHandler_deactivateListing,
  useRentalManagerUserPropertyDetailsWebApiHandler_details,
  useRentalManagerUserPropertyDetailsWebApiHandler_overview,
  useRentalManagerUserPropertySummaryWebApiHandler_listing,
} from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';
import { useSatelliteWebLandlordConversationHandler_addMessage } from '@zg-rentals/ts-satellite-client-react-query';

import { useGhostedLeads, useNotAvailableMessage } from './hooks';

import { DEACTIVATION_REASONS, STEPS } from './constants';

import AddTenantSelectStep from './components/AddTenantSelectStep';
import DeactivationReasonStep from './components/DeactivationReasonStep';
import NotifyLeads from './components/NotifyLeads';
import UpsellStep from './components/UpsellStep';

import {
  ADD_TENANT_STEP_CLICK_EVENT_ID,
  ADD_TENANT_STEP_TRIGGER_SOURCE,
  emitAddTenantStepClickEvent,
  emitDeactivationReasonClickEvent,
  emitLeaseAndPaymentUpsellStepClickEvent,
  LEASE_AND_PAYMENT_UPSELL_MODAL_CLICK_EVENT_ID,
  LEASE_AND_PAYMENT_UPSELL_TRIGGER_SOURCE,
} from '../../../analytics/events/tenantDirectory';
import { useAuth } from '../../AuthProvider';

import type { TenantSelectFormBodyRef } from '@zg-rentals/modal-tenant';
import type { RentalManagerDeactivateListingReason } from '@zg-rentals/ts-rental-manager-api-beta-client';
import type { DeactivationReason, Step } from './constants';

type DeactivateListingModalProps = {
  listingAlias: string;
  open?: boolean;
  onOpenChange: (open: boolean) => void;
};

const ERROR_MESSAGES = {
  DEFAULT: 'There was an error. Please try again',
};

const DeactivateListingModal = ({ listingAlias, open = false, onOpenChange }: DeactivateListingModalProps) => {
  const queryClient = useQueryClient();
  const { enqueueToast } = useToast();

  const [step, setStep] = useState<Step>(STEPS.DEACTIVATION_REASON);
  const [deactivationReason, setDeactivationReason] = useState<DeactivationReason | null>(null);
  const [isAddTenantFormValid, setIsAddTenantFormValid] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tenantSelectFormBodyRef = useRef<TenantSelectFormBodyRef>(null);

  const { user } = useAuth();

  const { data: propertyDetails } = useRentalManagerUserPropertyDetailsWebApiHandler_details(
    { listingAlias },
    { enabled: Boolean(listingAlias) },
  );

  const { ghostedLeads, toggleIncludeLead } = useGhostedLeads(listingAlias, propertyDetails?.isMultifamily);
  const { notAvailableMessage, setNotAvailableMessage } = useNotAvailableMessage(
    propertyDetails?.street,
    propertyDetails?.contactName,
  );

  const { mutate: deactivateListing, isPending } =
    useRentalManagerUserPropertyDeactivateWebApiHandler_deactivateListing();

  const { mutateAsync: addMessageAsync } = useSatelliteWebLandlordConversationHandler_addMessage();

  const getIsPrimaryCTADisabled = (): boolean => {
    if (isPending) return true;

    switch (step) {
      case STEPS.DEACTIVATION_REASON: {
        return deactivationReason === null;
      }
      case STEPS.ADD_TENANT_SELECT: {
        return !isAddTenantFormValid;
      }
      case STEPS.NOTIFY_LEADS: {
        const noLeadsSelected = ghostedLeads.filter(({ isIncluded }) => isIncluded).length === 0;

        return noLeadsSelected;
      }
      default: {
        return true;
      }
    }
  };

  const getModalConfig = (): { heading: string; primaryButtonLabel: string; secondaryButtonLabel: string } => {
    switch (step) {
      case STEPS.ADD_TENANT_SELECT:
        return { heading: 'Your tenants', primaryButtonLabel: 'Save & continue', secondaryButtonLabel: 'Skip' };
      case STEPS.NOTIFY_LEADS:
        return {
          heading: 'Notify renters who you haven’t responded to yet?',
          primaryButtonLabel: 'Send and continue',
          secondaryButtonLabel: 'Skip',
        };
      case STEPS.UPSELL:
        return {
          heading: 'Next steps to make this move easier with Zillow',
          primaryButtonLabel: '',
          secondaryButtonLabel: '',
        };
      case STEPS.DEACTIVATION_REASON:
      default:
        return {
          heading: 'Why are you deactivating this listing?',
          primaryButtonLabel: 'Deactivate listing',
          secondaryButtonLabel: 'Cancel',
        };
    }
  };

  const modalConfig = getModalConfig();
  const isPrimaryCTADisabled = getIsPrimaryCTADisabled();
  // 5 is the listing type code for "Room for Rent"
  const isRoomForRent = propertyDetails?.listingType?.code === 5;

  useEffect(() => {
    if (open) {
      return () => {
        resetState();
      };
    }
  }, [open]);

  const closeModal = ({
    triggerAddTenantStepCloseEvent = false,
    triggerUpsellCloseEvent = false,
  }: { triggerAddTenantStepCloseEvent?: boolean; triggerUpsellCloseEvent?: boolean } = {}) => {
    if (triggerAddTenantStepCloseEvent) {
      triggerCSAddTenantStepClick(ADD_TENANT_STEP_TRIGGER_SOURCE.CLOSE);
    }

    if (triggerUpsellCloseEvent) {
      triggerCSLeaseAndPaymentUpsellStepClick(LEASE_AND_PAYMENT_UPSELL_TRIGGER_SOURCE.CLOSE);
    }

    onOpenChange(false);
  };

  const getShouldNotifyLeads = (): boolean => {
    return deactivationReason !== DEACTIVATION_REASONS.STILL_LOOKING_PAUSING_ON_ZILLOW && ghostedLeads.length > 0;
  };

  const resetState = () => {
    setError(null);
    setIsAddTenantFormValid(false);
    setDeactivationReason(null);
    setStep(STEPS.DEACTIVATION_REASON);
  };

  const invalidatePropertyQueries = () => {
    queryClient.invalidateQueries({
      queryKey: useRentalManagerUserPropertyDetailsWebApiHandler_details.getKey({ listingAlias }),
    });
    queryClient.invalidateQueries({
      queryKey: useRentalManagerUserPropertyDetailsWebApiHandler_overview.getKey({ listingAlias }),
    });
    queryClient.invalidateQueries({
      queryKey: useRentalManagerUserPropertySummaryWebApiHandler_listing.getKey({ listingAlias }),
    });
    queryClient.invalidateQueries({ queryKey: ['getProperties'], refetchType: 'all' });
  };

  const navigateAfterNotifyLeadsStep = () => {
    if (isRoomForRent === false) {
      setStep(STEPS.UPSELL);
    } else {
      closeModal();

      // TODO: after this page is fully migrated, use v10 router.push with getDashboardOverviewRoute
      window.location.href = `/rental-manager/properties/${listingAlias}/dashboard/overview`;
    }
  };

  const navigateAfterTenantSelectStep = () => {
    const shouldNotifyLeads = getShouldNotifyLeads();

    if (shouldNotifyLeads) {
      setStep(STEPS.NOTIFY_LEADS);
    } else {
      navigateAfterNotifyLeadsStep();
    }
  };

  const triggerCSLeaseAndPaymentUpsellStepClick = (
    triggerSource: (typeof LEASE_AND_PAYMENT_UPSELL_TRIGGER_SOURCE)[keyof typeof LEASE_AND_PAYMENT_UPSELL_TRIGGER_SOURCE],
  ) => {
    if (!propertyDetails || !user) return;

    emitLeaseAndPaymentUpsellStepClickEvent({
      user,
      currentProperty: propertyDetails,
      triggerSource,
    });
  };

  const triggerCSAddTenantStepClick = (
    triggerSource: (typeof ADD_TENANT_STEP_TRIGGER_SOURCE)[keyof typeof ADD_TENANT_STEP_TRIGGER_SOURCE],
  ) => {
    if (!propertyDetails || !user) return;

    emitAddTenantStepClickEvent({
      user,
      currentProperty: propertyDetails,
      triggerSource,
    });
  };

  const triggerCSDeactivationReasonClick = (answer: DeactivationReason) => {
    if (!propertyDetails || !user) return;

    const triggerObjectMap: Record<DeactivationReason, 'found_new_tenant' | 'tenant_renewed' | 'still_looking'> = {
      [DEACTIVATION_REASONS.FOUND_RENTER_ELSEWHERE]: 'found_new_tenant',
      [DEACTIVATION_REASONS.TENANT_RENEWED]: 'tenant_renewed',
      [DEACTIVATION_REASONS.STILL_LOOKING_PAUSING_ON_ZILLOW]: 'still_looking',
    };

    const value = triggerObjectMap[answer];

    if (value) {
      emitDeactivationReasonClickEvent({
        user,
        currentProperty: propertyDetails,
        triggerObject: value,
      });
    }
  };

  const handleAnswerCardClick = (answer: DeactivationReason | null) => {
    setDeactivationReason(answer);
  };

  const handleSecondaryCTA = () => {
    switch (step) {
      case STEPS.DEACTIVATION_REASON: {
        closeModal();
        break;
      }
      case STEPS.ADD_TENANT_SELECT: {
        triggerCSAddTenantStepClick(ADD_TENANT_STEP_TRIGGER_SOURCE.SKIP);
        navigateAfterTenantSelectStep();
        break;
      }
      case STEPS.NOTIFY_LEADS: {
        navigateAfterNotifyLeadsStep();
        break;
      }
      default: {
        closeModal();
      }
    }
  };

  const handleDeactivationReasonStep = () => {
    if (!deactivationReason) return;

    deactivateListing(
      {
        postData: {
          listingAlias,
          reason: deactivationReason as RentalManagerDeactivateListingReason,
        },
      },
      {
        onSuccess: () => {
          triggerCSDeactivationReasonClick(deactivationReason);
          enqueueToast(<Toast tone="success">Listing deactivated successfully</Toast>);

          setError(null);
          invalidatePropertyQueries();

          if (deactivationReason === DEACTIVATION_REASONS.STILL_LOOKING_PAUSING_ON_ZILLOW) {
            closeModal();

            return;
          }

          setStep(STEPS.ADD_TENANT_SELECT);
        },
        onError: () => {
          setError(ERROR_MESSAGES.DEFAULT);
        },
      },
    );
  };

  const handleAddTenantSelectStep = async () => {
    triggerCSAddTenantStepClick(ADD_TENANT_STEP_TRIGGER_SOURCE.SAVE_AND_CONTINUE);

    if (tenantSelectFormBodyRef.current) {
      // error handling is self contained within the TenantSelectFormBody component
      await tenantSelectFormBodyRef.current.addRenters();
    }
  };

  const handleNotifyLeads = async () => {
    const requests = ghostedLeads.map(({ conversationId, isIncluded }) => {
      if (isIncluded) {
        return addMessageAsync({ postData: { conversationId, message: notAvailableMessage } });
      }
      return Promise.resolve();
    });

    return Promise.all(requests).catch(() => setError(ERROR_MESSAGES.DEFAULT));
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      closeModal({
        triggerAddTenantStepCloseEvent: step === STEPS.ADD_TENANT_SELECT,
        triggerUpsellCloseEvent: step === STEPS.UPSELL,
      });
      return;
    }

    onOpenChange(isOpen);
  };

  const handlePrimaryCTA = async () => {
    // reset error
    setError(null);

    switch (step) {
      case STEPS.DEACTIVATION_REASON: {
        handleDeactivationReasonStep();
        break;
      }
      case STEPS.ADD_TENANT_SELECT: {
        handleAddTenantSelectStep();
        break;
      }
      case STEPS.NOTIFY_LEADS: {
        await handleNotifyLeads();

        navigateAfterNotifyLeadsStep();
        break;
      }
      default: {
        break;
      }
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case STEPS.DEACTIVATION_REASON:
        return (
          <DeactivationReasonStep deactivationReason={deactivationReason} onAnswerSelect={handleAnswerCardClick} />
        );
      case STEPS.ADD_TENANT_SELECT:
        return (
          <AddTenantSelectStep
            listingAlias={listingAlias}
            deactivationReason={deactivationReason ?? undefined}
            ref={tenantSelectFormBodyRef}
            onValidationChange={setIsAddTenantFormValid}
            onSubmitSuccess={() => {
              navigateAfterTenantSelectStep();
            }}
          />
        );
      case STEPS.NOTIFY_LEADS:
        return (
          <NotifyLeads
            hideHeading={true}
            leads={ghostedLeads}
            message={notAvailableMessage}
            setMessage={setNotAvailableMessage}
            toggleIncludeLead={toggleIncludeLead}
            currentProperty={propertyDetails}
            user={user ?? undefined}
          />
        );
      case STEPS.UPSELL:
        return (
          <UpsellStep
            listingAlias={listingAlias}
            onCTAClick={closeModal}
            onClickstreamEvent={triggerCSLeaseAndPaymentUpsellStepClick}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      data-testid="DeactivateListingModal"
      data-eventid={
        step === STEPS.ADD_TENANT_SELECT
          ? ADD_TENANT_STEP_CLICK_EVENT_ID
          : step === STEPS.UPSELL
            ? LEASE_AND_PAYMENT_UPSELL_MODAL_CLICK_EVENT_ID
            : undefined
      }
      open={open}
      onOpenChange={handleOpenChange}
      header={
        <Heading level={2} textStyle="responsive.heading-xs" css={{ textAlign: 'left' }}>
          {modalConfig.heading}
        </Heading>
      }
      body={
        <>
          {error && (
            <Box css={{ marginBottom: 'tightest' }}>
              <Alert tone="warning">{error}</Alert>
            </Box>
          )}

          {renderStepContent()}
        </>
      }
      footer={
        step !== STEPS.UPSELL ? (
          <ButtonGroup aria-label="deactivation actions">
            <TextButton
              data-testid="CancelButton"
              css={{ paddingLeft: '400', paddingRight: '400' }}
              onClick={handleSecondaryCTA}
              disabled={isPending}
            >
              {modalConfig.secondaryButtonLabel}
            </TextButton>

            <Button
              data-testid="PrimaryCTAButton"
              emphasis="filled"
              tone="brand"
              onClick={handlePrimaryCTA}
              disabled={isPrimaryCTADisabled}
              loading={isPending}
            >
              {modalConfig.primaryButtonLabel}
            </Button>
          </ButtonGroup>
        ) : undefined
      }
      size={{ base: 'fullScreen', md: 'sm' }}
    />
  );
};

export default DeactivateListingModal;
